import { ReactElement } from "react";
import { ItemLayout } from "./ItemLayout";
import {
	DateString,
	Highlight,
	HighlightedText,
	PipeSeparator,
	Typography
} from "client/Components";
import { GlobalSearchScopeIdentifier, IntegrationFlowGlobalSearchResultItem } from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { useHighlights } from "client/Features/Layout/TitleBar/GlobalSearch/ResultView/CategoryView/ItemView/useHighlights";
import {
	generateTrackingId,
	isNotNil,
	isNotNilOrWhiteSpace
} from "client/Misc/utils";
import { TimeZone } from "client/DateAndTime/timezones";
import { AppItemIcon } from "./AppItemView";
import { localize } from "client_legacy/ndaw/culture";
import { useIntegrationFlowNavigationHandler } from "client/Features/Layout/TitleBar/GlobalSearch/Navigation/useIntegrationFlowNavigationHandler";
import { SearchListItem } from "../SearchListItem";
import { useKeyboardListNavigationItem } from "client/Hooks";
import { Key } from "client/Misc/commonTypes";

export type IntegrationFlowItemViewProps = {
	item: IntegrationFlowGlobalSearchResultItem;
	navigationIndex: number;
	index: number;
	itemScope: GlobalSearchScopeIdentifier;
	timezone: TimeZone;
};

export const IntegrationFlowItemView = (props: IntegrationFlowItemViewProps): ReactElement => {
	const {
		item,
		navigationIndex,
		index,
		itemScope,
		timezone
	} = props;

	const { link } = item;

	const executeHandler: () => void = useIntegrationFlowNavigationHandler({
		link,
		searchResult: item,
		itemScope
	});

	return (
		<SearchListItem
			onExecute={executeHandler}
			trackingId={generateTrackingId("integration-flow-item", `|${index}`)}
		>
			<IntegrationFlowItem
				item={item}
				timezone={timezone}
				navigationIndex={navigationIndex}
				onExecute={executeHandler}
			/>
		</SearchListItem>
	);
};
IntegrationFlowItemView.displayName = "IntegrationFlowItemView";

type IntegrationFlowItemProps = {
	item: IntegrationFlowGlobalSearchResultItem;
	timezone: TimeZone;
	navigationIndex: number;
	onExecute: () => void;
};

const IntegrationFlowItem = (props: IntegrationFlowItemProps): ReactElement => {
	const {
		item,
		timezone,
		navigationIndex,
		onExecute
	} = props;

	const {
		title,
		description,
		dateTime,
		subTitle1,
		subTitle2,
		hitHighlights,
		color,
		icon
	} = item;

	const highlights: Map<string, Highlight> = useHighlights(hitHighlights);

	const { ref, onKeyDown } = useKeyboardListNavigationItem({
		itemIndex: navigationIndex,
		onExecute: onExecute,
		executeKey: [Key.Enter, Key.Space],
	});

	return (
		<ItemLayout trackingId="integration-flow-item"
			ref={ref}
			onKeyDown={onKeyDown}
			tabIndex={-1}
		>
			<ItemLayout.IconContainer>
				<AppItemIcon
					color={color}
					image={icon}
				/>
			</ItemLayout.IconContainer>
			<ItemLayout.InfoWrapper>
				<ItemLayout.Title
					trackingId="title"
					title={title ?? ""}
				>
					{isNotNilOrWhiteSpace(title)
						? (
							<HighlightedText
								text={title}
								highlight={highlights.get("title")}
							/>
						)
						: (
							<Typography isItalic>
								{localize("_NoTitle")}
							</Typography>
						)
					}

				</ItemLayout.Title>

				<ItemLayout.Info>
					<ItemLayout.SubTitle>
						{isNotNil(dateTime) && (
							<DateString
								format="RelativeDayWithTime"
								value={dateTime}
								timezone={timezone}
							/>
						)}
						<TextSeparator
							previousValue={dateTime}
							followingValues={[subTitle1, subTitle2]}
						/>
						<HighlightedText
							text={subTitle1}
							highlight={highlights.get("subTitle1")}
						/>
						<TextSeparator
							previousValue={subTitle1}
							followingValues={[subTitle2]}
						/>
						<HighlightedText
							text={subTitle2}
							highlight={highlights.get("subTitle2")}
						/>
					</ItemLayout.SubTitle>
				</ItemLayout.Info>
				{isNotNil(description) && (
					<ItemLayout.Content>
						<HighlightedText
							text={description}
							highlight={highlights.get("description")}
						/>
					</ItemLayout.Content>
				)}
			</ItemLayout.InfoWrapper>
		</ItemLayout>
	);
};
IntegrationFlowItem.displayName = "IntegrationFlowItem";

type TextSeparatorProps = {
	previousValue: Date | string | nil;
	followingValues: readonly (Date | string | nil)[];
};

const TextSeparator = (props: TextSeparatorProps): ReactElement | null => {
	const {
		previousValue,
		followingValues
	} = props;

	const isVisible: boolean = Boolean(previousValue)
		&& followingValues.some(Boolean);

	return isVisible
		? (
			<PipeSeparator />
		)
		: null;
};
TextSeparator.displayName = "TextSeparator";